// ---------------------------------------------------------------------------
// src/config/patientStatus.js
//
// Single source of truth for patient status labels, colors, groupings,
// and the StatusBadge component.
// Imported by: PatientsPage, PatientChartPage, StatusTransitionModal
//
// Colors sourced from confirmed Nephros palette (Color System v2.1).
// All text/background combinations verified WCAG AA compliant (4.5:1 minimum).
//
//   Active       — teal-green #0F6E3A on #DCFCE7       (~5.2:1) ✓  (unchanged — under review)
//   Transplant   — white #FFFFFF on cobalt #154D9D      (~5.9:1) ✓
//   ESRD-HD      — white #FFFFFF on magenta #9D1985     (~5.1:1) ✓
//   ESRD-HHD     — white #FFFFFF on magenta #9D1985     (~5.1:1) ✓
//   ESRD-PD      — white #FFFFFF on magenta #9D1985     (~5.1:1) ✓
//   Hospice      — white #FFFFFF on mid-purple #6B2FA0  (~6.2:1) ✓
//   Deceased     — white #FFFFFF on charcoal #374151    (~8.6:1) ✓
//   Inactive     — charcoal #1F2937 on light grey #D1D5DB (~10.1:1) ✓
// ---------------------------------------------------------------------------

import React from 'react';

export const STATUS_CONFIG = {
  active:               { label: 'Active',             color: '#0F6E3A', bg: '#DCFCE7' },
  esrd_transplant:      { label: 'Transplant',         color: '#FFFFFF', bg: '#154D9D' },
  esrd_hd:              { label: 'ESRD-HD',            color: '#FFFFFF', bg: '#9D1985' },
  esrd_hhd:             { label: 'ESRD-HHD',           color: '#FFFFFF', bg: '#9D1985' },
  esrd_pd:              { label: 'ESRD-PD',            color: '#FFFFFF', bg: '#9D1985' },
  esrd_hospice:         { label: 'Hospice',            color: '#FFFFFF', bg: '#6B2FA0' },
  deceased:             { label: 'Deceased',           color: '#FFFFFF', bg: '#374151' },
  inactive_lost:        { label: 'Lost to F/U',        color: '#1F2937', bg: '#D1D5DB' },
  inactive_transferred: { label: 'Transferred',        color: '#1F2937', bg: '#D1D5DB' },
  inactive_discharged:  { label: 'Discharged',         color: '#1F2937', bg: '#D1D5DB' },
  inactive_moved:       { label: 'Moved',              color: '#1F2937', bg: '#D1D5DB' },
};

// Shared StatusBadge component — use this everywhere a status pill is rendered.
// Keeping it here means font weight, size, and padding are changed in one place.
export function StatusBadge({ status }) {
  const config = STATUS_CONFIG[status] || { label: status, color: '#1F2937', bg: '#D1D5DB' };
  return (
    <span style={{
      backgroundColor: config.bg,
      color: config.color,
      padding: '2px 8px',
      borderRadius: 'var(--radius-full)',
      fontSize: 'var(--font-size-xs)',
      fontWeight: 'var(--font-weight-medium)',
      whiteSpace: 'nowrap',
    }}>
      {config.label}
    </span>
  );
}

// Status groups — used by StatusTransitionModal for the grouped dropdown.
// Full descriptive labels here since they appear in a form context.
export const STATUS_GROUPS = [
  {
    label: 'Inactive',
    options: [
      { value: 'inactive_lost',        label: 'Lost to Follow-up' },
      { value: 'inactive_transferred', label: 'Transferred to Another Practice' },
      { value: 'inactive_discharged',  label: 'Discharged' },
      { value: 'inactive_moved',       label: 'Moved Out of Area' },
    ],
  },
  {
    label: 'ESRD',
    options: [
      { value: 'esrd_hd',         label: 'In-Center Hemodialysis' },
      { value: 'esrd_hhd',        label: 'Home Hemodialysis' },
      { value: 'esrd_pd',         label: 'Peritoneal Dialysis' },
      { value: 'esrd_transplant', label: 'Kidney Transplant' },
      { value: 'esrd_hospice',    label: 'Hospice / Palliative Care' },
    ],
  },
  {
    label: 'Other',
    options: [
      { value: 'active',   label: 'Active (return to active care)' },
      { value: 'deceased', label: 'Deceased' },
    ],
  },
];

// Flat label lookup — used by StatusTransitionModal for current status display
// and warning messages.
export const STATUS_LABELS = STATUS_GROUPS.reduce((acc, group) => {
  group.options.forEach(o => { acc[o.value] = o.label; });
  return acc;
}, {});